import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

import { useUserContext } from "@hooks/useUserContext";
import { getContentLanguage } from "@utils/tracking/contentLanguage";
import { getContentTypeFromPath } from "@utils/tracking/contentType";
import type { SaTrackingParams } from "../types/TrackingTypes";
import { ContentType, TrackingParam, UserType } from "../constants";

/**
 * Keeps `globalThis.sa_metadata` in sync with the current user and route and
 * fires a Simple Analytics pageview for each new path. The metadata is appended
 * to every pageview via the script's `data-metadata-collector`:
 *
 * - `upei`: the user's unique identifier.
 * - `user_type`: "leader" when the user is in the leadership group, else "employee".
 * - `content_type`: derived from the pathname (see {@link getContentTypeFromPath}).
 * - `content_language`: derived from the pathname and user prefs (see {@link getContentLanguage}).
 * - `article_cluster`: only on article pages, the cluster the article belongs to.
 *
 * Nothing runs while the user is loading, so metadata is set once with the
 * correct values. Each path is tracked at most once. When the async Simple
 * Analytics script has not loaded yet (e.g. a hard refresh), the path is queued
 * in `sa_pending_path` for `saLoaded()` to flush once the script is ready.
 */
export const useSaMetadata = (trackingParams?: SaTrackingParams) => {
  const { pathname } = useLocation();
  const { user, isLoading } = useUserContext();

  // Prevents duplicate pageviews when the effect re-runs for the same path
  // (e.g. the user object reference changes but the route does not).
  const lastTrackedPath = useRef<string | null>(null);

  useEffect(() => {
    // Wait until the user context resolves so metadata is set once, correctly.
    if (isLoading) {
      return;
    }

    // Keep sa_metadata in sync so Simple Analytics appends it to the pageview.
    if (user) {
      const contentType = getContentTypeFromPath(pathname);

      globalThis.sa_metadata = {
        [TrackingParam.userType]: user.isLeader
          ? UserType.leader
          : UserType.employee,
        [TrackingParam.contentType]: contentType,
        [TrackingParam.contentLanguage]: getContentLanguage(
          pathname,
          user.languagePreference,
          user.autoTranslateEnabled,
          user.autoTranslateLanguage
        ),
        [TrackingParam.upei]: user.id,
        ...(contentType === ContentType.article &&
        trackingParams?.articleCluster
          ? { [TrackingParam.articleCluster]: trackingParams.articleCluster }
          : {}),
      };
    } else {
      globalThis.sa_metadata = {};
    }

    // Only fire once per path.
    if (lastTrackedPath.current === pathname) {
      return;
    }
    lastTrackedPath.current = pathname;

    if (globalThis.sa_pageview) {
      // Script ready: fire immediately with the metadata set above.
      globalThis.sa_pageview(pathname);
    } else {
      // On a hard refresh the hook can run before the async SA script loads.
      // Queue the path so saLoaded() flushes it once the script is ready.
      globalThis.sa_pending_path = pathname;
    }
  }, [pathname, user, isLoading, trackingParams]);
};
